import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, Mountain, Youtube } from "lucide-react";
import { ShamanicGallery } from "../components/ShamanicGallery";
import { Footer } from "../components/Footer";

const PERU_PARAGRAPHS = [
  "Peru, the plants, the elders, and the work I carry forward....",
  "It was love at first sight. From the very first moment I connected with the shamanic world, I fell in love and knew this was my path.",
  "People think that if you take a few courses, attend ceremonies, or read some books, you become a shaman. And it's sad. A lot of harm is done because of this. You don't become a shaman, you are born one, and a shaman is shaped from birth through experiences and... a lot of pain. Initiation into shamanism can only be done through a shamanic lineage, by Amazonian abuelos (grandfathers/elders). They carry the ancient knowledge, and you must go to them to know the sacred medicine; they are the ones who tell you whether or not you can work as a shaman, whether you can serve the plants. Even so, it is a process that takes time—years. I didn't skip any steps, but I was fortunate enough to walk through some of them quickly.",
  "I was blessed to be found by a Shipibo master like no other, the last of his lineage. He planted a seed in me that continues to grow. Becoming a shaman is the hardest thing I have ever experienced, and it requires, above all, healing yourself before helping others to heal. But I cannot find a path that feels closer to my heart, or a way to be a light to the world.",
  "That is what gives me purpose.",
];

const MISSION_PARAGRAPHS = [
  "I didn't choose to be a shaman. Neither do techniques make you one. Life chose me — through darkness, pain, collapse, loss, and moments when no one could save me anymore.",
  "My purpose here is to become the man I didn't have beside me when I was falling apart. Not to play the role of a shaman, to be a teacher, or to be followed by anyone. But to be the one who opens the space around him. A man to whom, when someone arrives broken, they don't receive advice, techniques, or explanations — but presence. That space in which the soul remembers who it is.",
  "This is my calling, and ironically, I ran from it my whole life, drowning my senses. I wanted to stop feeling, not knowing that this is the very ability of the shaman.",
  "Later, I wanted to understand why. Why was I chosen? Maybe it's because I have carried, and still carry, a pain that others couldn't — and I carried it without turning against the world. Or perhaps because I hold for humanity a love that is deep and sincere.",
];

export default function ShamanicPath() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="shamanic-page"
      className="relative z-10 min-h-screen"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(122,158,109,0.18), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(214,181,109,0.12), transparent 60%), #06040A",
      }}
    >
      <header className="sticky top-0 z-50 bg-[#06040A]/80 backdrop-blur-xl border-b border-[#2C223A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="shamanic-logo">
            <img src="/ovidiu-logo.png" alt="Ovidiu" className="h-12 md:h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="shamanic-back-home"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-[#A59DAB] hover:text-[#D6B56D] transition-colors"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 pt-20 md:pt-28 pb-14 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
        >
          <h1 className="font-heading font-light text-5xl md:text-7xl text-[#EBE4D8] leading-[1.02]">
            Shamanic <span className="italic text-[#D6B56D]">Path</span>
          </h1>
          <p
            data-testid="shamanic-motto"
            className="font-heading italic text-lg md:text-2xl text-[#D6B56D] mt-10 max-w-2xl mx-auto leading-relaxed"
          >
            “I didn't come here to play at healing. I am here because my light will change the world.”
          </p>
        </motion.div>
      </section>

      {/* FEATURED PORTRAIT + PERU JOURNAL */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-14 md:py-20">
        <div className="grid md:grid-cols-12 md:gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9 }}
            data-testid="shamanic-portrait"
            className="relative mx-auto w-full max-w-sm md:max-w-none md:col-span-5 md:sticky md:top-28"
          >
            <div className="relative aspect-square w-full">
              <img
                src="https://customer-assets.emergentagent.com/job_b9e021d3-e729-4e4e-a5aa-aa23768687ba/artifacts/wxnhtde3_alingavrila.ro%20-145.jpg"
                alt="With the Shipibo maestro"
                style={{ transform: "rotate(-90deg)" }}
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            data-testid="shamanic-peru-section"
            className="mt-10 md:mt-0 md:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6 text-[#D6B56D]">
              <Mountain size={20} />
              <span className="text-xs font-bold uppercase tracking-[0.3em]">Journal — Peru</span>
            </div>
            <h2 className="font-heading italic text-3xl md:text-5xl text-[#EBE4D8] mb-8">
              Love at first <span className="text-[#D6B56D]">sight</span>
            </h2>
            <div className="space-y-5 text-[#C8C0BE] leading-relaxed text-base md:text-lg font-light">
              {PERU_PARAGRAPHS.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SHAMANIC GALLERY (separate album, admin-managed from this page) */}
      <ShamanicGallery />

      {/* MISSION */}
      <section className="max-w-3xl mx-auto px-6 py-14 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="shamanic-mission-section"
          className="relative rounded-3xl border border-[#D6B56D]/25 bg-[#0F1410]/60 backdrop-blur-xl p-8 md:p-14"
        >
          <div className="flex items-center gap-3 mb-6 text-[#D6B56D]">
            <Flame size={20} />
            <span className="text-xs font-bold uppercase tracking-[0.3em]">My mission</span>
          </div>
          <h2 className="font-heading italic text-3xl md:text-5xl text-[#EBE4D8] mb-8">
            The path I <span className="text-[#D6B56D]">hold</span>
          </h2>
          <div className="space-y-5 text-[#C8C0BE] leading-relaxed text-base md:text-lg font-light">
            {MISSION_PARAGRAPHS.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <Link
            to="/"
            data-testid="shamanic-bottom-home"
            className="inline-flex items-center gap-3 px-9 py-3.5 rounded-full border border-[#D6B56D]/60 text-[#EBE4D8] text-sm tracking-widest uppercase hover:bg-[#D6B56D]/10 hover:border-[#D6B56D] transition-all duration-300"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </section>

      {/* PODCAST / YOUTUBE EMBED */}
      <section
        data-testid="shamanic-podcast-section"
        className="max-w-5xl mx-auto px-6 pb-20 md:pb-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-3 mb-5 text-[#D6B56D]">
            <Youtube size={18} />
            <span className="text-xs font-bold uppercase tracking-[0.3em]">
              On the podcast
            </span>
          </div>
          <h2 className="font-heading font-light text-4xl md:text-5xl text-[#EBE4D8]">
            Talking about <span className="italic text-[#D6B56D]">shamanism</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="relative rounded-3xl overflow-hidden border border-[#D6B56D]/25 shadow-[0_20px_80px_-20px_rgba(214,181,109,0.35)]"
        >
          <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
            <iframe
              data-testid="shamanic-youtube-iframe"
              src="https://www.youtube.com/embed/ikjYKccNjgQ?start=3564&rel=0"
              title="Ovidiu Stefan on the shamanic path — podcast"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
